import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';

export interface UserInfo {
  name: string;
  role: string;
  email?: string;
  userId?: number;
}

interface StoredSession {
  token: string;
  user: UserInfo | null;
  loggedInAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly TOKEN_KEY = 'auth_token';
  private readonly USER_KEY = 'auth_user';
  private readonly SESSION_KEY = 'auth_session';

  private userSubject = new BehaviorSubject<UserInfo | null>(null);
  user$ = this.userSubject.asObservable();

  constructor(private router: Router) {
    this.userSubject.next(this.restoreSession());
  }

  setToken(token: string): void {
    if (!token) return;
    localStorage.setItem(this.TOKEN_KEY, token);

    const existing = this.getUser();
    if (!existing || existing.name?.includes('|') || existing.role?.includes('|')) {
      const decoded = this.decodeToken(token);
      if (decoded) {
        const role = decoded.role || decoded.roles || decoded.authorities || decoded.userType || '';
        const name = decoded.name || decoded.sub || decoded.username || decoded.userName || 'User';
        const email = decoded.email || decoded.emailId || '';
        this.setUser({ name, role, email });
        return;
      }
    }

    this.persistSession();
  }

  getToken(): string | null {
    const token = localStorage.getItem(this.TOKEN_KEY);
    if (token) return token;
    const sessionToken = this.readStoredSession()?.token;
    if (sessionToken) {
      localStorage.setItem(this.TOKEN_KEY, sessionToken);
      return sessionToken;
    }
    return null;
  }

  removeToken(): void {
    localStorage.removeItem(this.TOKEN_KEY);
  }

  private persistSession(): void {
    try {
      const previous = this.readStoredSession();
      const rawUser = localStorage.getItem(this.USER_KEY);
      const session: StoredSession = {
        token: localStorage.getItem(this.TOKEN_KEY) || '',
        user: rawUser ? (JSON.parse(rawUser) as UserInfo) : previous?.user || null,
        loggedInAt: previous?.loggedInAt || Date.now(),
      };
      localStorage.setItem(this.SESSION_KEY, JSON.stringify(session));
    } catch {}
  }

  private readStoredSession(): StoredSession | null {
    try {
      const raw = localStorage.getItem(this.SESSION_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as StoredSession;
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  restoreSession(): UserInfo | null {
    try {
      const session = this.readStoredSession();

      if (!localStorage.getItem(this.TOKEN_KEY) && session?.token) {
        localStorage.setItem(this.TOKEN_KEY, session.token);
      }

      let user = this.getUser();
      const token = this.getToken();
      if (!user && token) {
        const decoded = this.decodeToken(token);
        if (decoded) {
          user = this.cleanUser({
            name: decoded.name || decoded.sub || decoded.username || decoded.userName || 'User',
            role: decoded.role || decoded.roles || decoded.authorities || decoded.userType || '',
            email: decoded.email || decoded.emailId || '',
          });
        }
      }

      if (token || user) {
        if (user) {
          localStorage.setItem(this.USER_KEY, JSON.stringify(user));
        }
        this.persistSession();
      }

      return user;
    } catch {
      return null;
    }
  }

  cleanUser(user: Partial<UserInfo>): UserInfo {
    let name = (user.name || '').trim();
    let role = (user.role || '').trim();
    let email = (user.email || '').trim();

    const pipeSource = [name, role, email].find(s => s && s.includes('|')) || '';
    if (pipeSource) {
      const parts = pipeSource.split('|').map(p => p.trim()).filter(Boolean);
      if (parts.length >= 2) {
        if (/^\d+$/.test(parts[0])) {
          name = parts[1] || name;
          role = parts[2] || role;
        } else {
          name = parts[0] || name;
          role = parts[1] || role;
        }
      }
    }

    if (name.includes('|')) {
      const nParts = name.split('|').map(p => p.trim()).filter(Boolean);
      name = nParts.find(p => !/^\d+$/.test(p)) || nParts[0] || 'User';
    }

    return {
      name: name || 'User',
      role: this.formatRole(role),
      email: email.includes('|') ? '' : email
    };
  }

  setUser(user: UserInfo): void {
    const cleaned = this.cleanUser(user);
    localStorage.setItem(this.USER_KEY, JSON.stringify(cleaned));
    this.persistSession();
    this.userSubject.next(cleaned);
  }

  formatRole(role: any): string {
    if (Array.isArray(role)) {
      role = role[0] || '';
    }
    if (typeof role === 'object' && role !== null) {
      role = role.authority || role.role || role.name || role.userType || '';
    }
    if (typeof role === 'string') {
      return role.replace(/^ROLE_/i, '');
    }
    return '';
  }

  decodeToken(token: string): any {
    try {
      const parts = token.split('.');
      if (parts.length === 3) {
        const base64Url = parts[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split('')
            .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        );
        return JSON.parse(jsonPayload);
      }
    } catch {
      return null;
    }
    return null;
  }

  getUser(): UserInfo | null {
    let user: UserInfo | null = null;
    const raw = localStorage.getItem(this.USER_KEY);
    if (raw) {
      try {
        user = JSON.parse(raw) as UserInfo;
      } catch {}
    }

    if (!user || user.name?.includes('|') || user.role?.includes('|')) {
      const token = this.getToken();
      if (token) {
        const decoded = this.decodeToken(token);
        if (decoded) {
          const rawRole = decoded.role || decoded.roles || decoded.authorities || decoded.userType || '';
          const rawName = decoded.name || decoded.sub || decoded.username || decoded.userName || 'User';
          const rawEmail = decoded.email || decoded.emailId || '';
          user = { name: rawName, role: rawRole, email: rawEmail };
        }
      }
    }

    if (user) {
      const cleaned = this.cleanUser(user);
      if (raw && (user.name?.includes('|') || user.role?.includes('|'))) {
        localStorage.setItem(this.USER_KEY, JSON.stringify(cleaned));
      }
      return cleaned;
    }

    return null;
  }

  isAuthenticated(): boolean {
    return !!this.getToken() || !!this.getUser();
  }

  isAdmin(): boolean {
    if (!this.isAuthenticated()) return false;
    const user = this.getUser();
    const role = (user?.role || '').toUpperCase();
    const name = (user?.name || '').toLowerCase();
    return role.includes('ADMIN') || name === 'admin';
  }

  logout(): void {
    this.removeToken();
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.SESSION_KEY);
    this.userSubject.next(null);
    this.router.navigate(['/auth/login']);
  }
}
